// Stock > Transferências armazém <-> lojas — cliente da API interna (sessão).
import axios from 'axios';

const BASE_URL = '/api/stock/transfers';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  withCredentials: true,
});

// Sessão expirada -> login (mesmo comportamento do resto da app)
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export type TransferGroup = 'sku' | 'sku_loja';
export type TransferShow = 'ciclos' | 'sem_venda' | 'pares';
export type TransferTipo = 'IDA' | 'VOLTA' | 'ENTRE_LOJAS' | 'OUTRO';

export interface TransferFilters {
  start?: string;
  end?: string;
  stores?: string[];
  cat1?: string;
  search?: string;
}

export interface TransferProductsQuery extends TransferFilters {
  group?: TransferGroup;
  show?: TransferShow;
  ordering?: string;
  limit?: number;
  offset?: number;
}

/** Contagens aditivas (por loja, por mês ou total). */
export interface TransferCounts {
  idas: number;
  un_idas: number;
  valor_idas: number;
  voltas: number;
  un_voltas: number;
  valor_voltas: number;
  pares: number;
  sem_venda: number;
  un_sem_venda: number;
  valor_sem_venda: number;
  ciclos: number;
  un_ciclos: number;
  valor_ciclos: number;
  pct_sem_venda: number | null;
  pct_voltas: number | null;
  pct_ciclos: number | null;
}

export interface TransferTotals extends TransferCounts {
  produtos_ciclos: number | null;
  produtos_sem_venda: number | null;
  med_dias_ida_volta: number | null;
  med_dias_reenvio: number | null;
}

export interface TransferSummary {
  totals: TransferTotals;
  by_store: Array<TransferCounts & { loja: string }>;
  by_month: Array<TransferCounts & { mes: string }>;
  dias_ida_volta_hist: Array<{ bucket: string; n: number }>;
  by_category: Array<{ cat1: string; pares: number; sem_venda: number; ciclos: number; valor_ciclos: number; produtos: number }>;
  /** dow: 1 = domingo ... 7 = sábado (BigQuery) */
  by_weekday: Array<{ dow: number; voltas: number; sem_venda: number; ciclos: number }>;
  by_user: Array<{ utilizador: string; voltas: number; sem_venda: number; ciclos: number; lojas: string[] }>;
  flows: Array<{ origem: string; destino: string; tipo: TransferTipo; entregas: number; produtos: number; unidades: number; valor: number }>;
  cycle_days: number;
}

export interface TransferProductRow {
  sku: string;
  loja?: string;
  nome: string | null;
  cat1: string | null;
  cat2: string | null;
  estado: string | null;
  lojas: string[];
  pares: number;
  voltas_sem_venda: number;
  ciclos_inuteis: number;
  pct_sem_venda: number | null;
  unidades_sem_venda: number;
  unidades_ciclos: number;
  valor_sem_venda: number;
  valor_ciclos: number;
  med_dias_ida_volta: number | null;
  med_dias_reenvio: number | null;
  ultima_volta: string | null;
}

export interface TransferProductsResponse {
  count: number;
  limit: number;
  offset: number;
  group: TransferGroup;
  show: TransferShow;
  ordering: string;
  results: TransferProductRow[];
}

export interface TransferEvent {
  ts: string;
  data: string;
  tipo: TransferTipo;
  origem: string;
  destino: string;
  loja: string | null;
  entrega: string;
  qty: number;
  valor: number;
  utilizador: string | null;
  pedido_compra: string | null;
  prev_tipo: TransferTipo | null;
  prev_ts: string | null;
  dias_ida_volta: number | null;
  horas_ida_volta: number | null;
  vendidas_entre: number | null;
  sem_venda: boolean | null;
  dias_volta_reenvio: number | null;
  ciclo_inutil: boolean | null;
}

export interface TransferTimeline {
  sku: string;
  loja: string | null;
  product: { nome: string | null; cat1: string | null; cat2: string | null; estado: string | null } | null;
  events: TransferEvent[];
  by_store: Array<{ loja: string; idas: number; voltas: number; sem_venda: number; ciclos: number }>;
  truncated: boolean;
}

export interface TransferMeta {
  min_date: string | null;
  max_date: string | null;
  calculated_at: string | null;
  transferencias: number;
  cat1: string[];
  stores: string[];
  orderings: string[];
  cycle_days: number;
}

export const toParams = (q: TransferProductsQuery): Record<string, string> => {
  const params: Record<string, string> = {};
  Object.entries(q).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) params[k] = v.join(',');
    } else {
      params[k] = String(v);
    }
  });
  return params;
};

export const stockTransfersApi = {
  getMeta: async (): Promise<TransferMeta> => (await client.get('/meta/')).data,

  getSummary: async (f: TransferFilters): Promise<TransferSummary> =>
    (await client.get('/summary/', { params: toParams(f) })).data,

  getProducts: async (q: TransferProductsQuery): Promise<TransferProductsResponse> =>
    (await client.get('/products/', { params: toParams(q) })).data,

  getTimeline: async (sku: string, loja?: string): Promise<TransferTimeline> =>
    (await client.get(`/sku/${encodeURIComponent(sku)}/`, { params: loja ? { loja } : {} })).data,

  exportUrl: (q: TransferProductsQuery): string => {
    const { limit, offset, ...rest } = q;
    const qs = new URLSearchParams(toParams(rest)).toString();
    return `${BASE_URL}/products/export/${qs ? `?${qs}` : ''}`;
  },
};
